import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  afterEveryRender,
  afterRenderEffect,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
} from '@angular/router';
import { EzTabGroupComponent } from '@template-ui/components/ez-tab-group/ez-tab-group.component';
import { read } from '@template-ui/helpers';
import { TabConfig } from '@template-ui/types';

import { createTerminologyUsageSignals } from '@app/shared/terminology/terminology-usage-signals';

import { PreHearingCaptureComponent } from '../hearing-record/pre-hearing-capture.component';
import { SuitabilityHearingComponent } from '../hearing-record/suitability-hearing.component';
import {
  HEARING_DETAIL_ROUTE_BASE,
  HearingPhase,
} from '../navigation/phms-detail-navigation';
import { PHMS_HEARING_TERMINOLOGY } from '../phms-hearing.terminology';
import { PostHearingWorkspaceComponent } from '../post-hearing/workspace/post-hearing-workspace.component';
import {
  HearingNavigationService,
  HearingSharedView,
} from './hearing-navigation.service';
import {
  HEARING_DETAILS_OPEN_VALUE,
  HEARING_DETAILS_QUERY_PARAM,
} from './hearing-page-header.component';
import { HearingSharedContentComponent } from './hearing-shared-content.component';
import { HearingWorkflowTaskAuthorityService } from './hearing-workflow-task-authority.service';

/** Test id prefix of the rendered tab elements: `hearing-tab-{section}` (#1530). */
export const HEARING_TAB_TEST_ID_PREFIX = 'hearing-tab-';

/**
 * Redirects the former shared hearing routes (`/sdmf`, `/documents`, `/activity`) onto the
 * Pre-Hearing page: the SDMF and Documents tabs are addressed by URL fragment, the hearing activity
 * lives behind the header's Details & history (`?details=open`). Query parameters are preserved.
 */
export function redirectSharedHearingView(
  view: HearingSharedView,
): CanActivateFn {
  return (route: ActivatedRouteSnapshot) => {
    const router = inject(Router);
    const proceedingId =
      route.parent?.paramMap.get('scheduledProceedingId') ??
      route.paramMap.get('scheduledProceedingId');
    const queryParams = { ...route.queryParams };
    if (view === 'activity') {
      queryParams[HEARING_DETAILS_QUERY_PARAM] = HEARING_DETAILS_OPEN_VALUE;
    }
    return router.createUrlTree(
      [HEARING_DETAIL_ROUTE_BASE, proceedingId, 'pre-hearing'],
      {
        queryParams,
        fragment: view === 'activity' ? undefined : view,
      },
    );
  };
}

/**
 * Route-owned content of one hearing phase page (#1530): the phase's sub-areas as tabs across the
 * top, the phase capture workspace beneath them, and the shared SDMF / Documents views hosted in
 * their own tab panels. The URL fragment is the tab key; the shell maps it onto the navigation
 * context, so the section editors follow the selected tab exactly as the former menu drove them.
 */
@Component({
  selector: 'phms-hearing-phase-content',
  standalone: true,
  imports: [
    CommonModule,
    EzTabGroupComponent,
    PreHearingCaptureComponent,
    SuitabilityHearingComponent,
    PostHearingWorkspaceComponent,
  ],
  templateUrl: './hearing-phase-content.component.html',
  styleUrl: './hearing-phase-content.component.scss',
})
export class HearingPhaseContentComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly taskAuthority = inject(HearingWorkflowTaskAuthorityService);
  readonly navigation = inject(HearingNavigationService);
  readonly hearingText = createTerminologyUsageSignals(
    PHMS_HEARING_TERMINOLOGY.hearingPhasePage,
  );
  private readonly tabHost = viewChild<ElementRef<HTMLElement>>('tabHost');
  private readonly tabGroup = viewChild(EzTabGroupComponent);
  private readonly tabGroupChanges = viewChild(EzTabGroupComponent, {
    read: ChangeDetectorRef,
  });
  private readonly preparationEditor = viewChild(PreHearingCaptureComponent);
  readonly phase = signal<HearingPhase>('pre');
  readonly scheduledProceedingId = signal<number | null>(null);
  readonly legalReviewId = signal<number | null>(null);
  /** Only an explicit compatibility result permits independent record acknowledgements. */
  readonly workflowManaged = computed(() => {
    if (this.taskAuthority.loading() || this.taskAuthority.error()) return true;
    const task = this.taskAuthority.snapshot();
    if (
      task?.link.availability !== 'unavailable' ||
      task.link.workflowInstanceId != null ||
      task.link.occurrenceId != null ||
      task.link.route != null
    )
      return true;
    return ![
      'hearing_link_missing',
      'workflow_link_missing',
      'workflow_adapter_unavailable',
    ].includes(task.link.reasonCode);
  });

  private readonly privateNotesHidden = computed(
    () => !this.navigation.privateNotesAllowed(),
  );

  /** The tabs of the routed phase, in the order the story lists them. Labels resolve through terminology. */
  readonly phaseTabs = computed<TabConfig[]>(() => {
    const documents: TabConfig = {
      label: this.hearingText.tabDocuments,
      fragment: 'documents',
      component: HearingSharedContentComponent,
      inputs: { view: 'documents' },
    };
    switch (this.phase()) {
      case 'at':
        return [
          {
            label: this.hearingText.tabParticipation,
            fragment: 'hearing-record',
          },
          { label: this.hearingText.tabRecordDecision, fragment: 'decision' },
          documents,
        ];
      case 'post':
        return [
          { label: this.hearingText.tabLegalReview, fragment: 'decision' },
          documents,
        ];
      default:
        return [
          { label: this.hearingText.tabHistoryRisk, fragment: 'case-review' },
          {
            label: this.hearingText.tabParticipants,
            fragment: 'victim-notification',
          },
          {
            label: this.hearingText.tabSdmf,
            fragment: 'sdmf',
            component: HearingSharedContentComponent,
            inputs: { view: 'sdmf' },
          },
          {
            label: this.hearingText.tabPrivateNotes,
            fragment: 'analysis-notes',
            hidden: this.privateNotesHidden,
          },
          documents,
        ];
    }
  });
  readonly visibleTabs = computed(() =>
    this.phaseTabs().filter((tab) => !read(tab.hidden)),
  );
  /** The section the navigation context resolved for this page: a phase section id or a shared view. */
  readonly activeSection = computed(() => this.navigation.section());
  /** True while the SDMF or Documents tab hosts the content; the phase workspace is not rendered then. */
  readonly sharedTabActive = computed(() => {
    const phase = this.navigation.context()?.phase;
    return phase === 'sdmf' || phase === 'documents';
  });

  constructor() {
    // The tab group selects from the URL fragment on its own. A page opened without one (a bare
    // phase URL, a legacy ?section= link or its alias, ?legalReview=) still shows the resolved
    // section's tab as selected. The index is written after render and the tab group is marked
    // for the next pass, so the write never lands inside the pass that checked its binding.
    afterRenderEffect(() => {
      const group = this.tabGroup();
      const index = this.visibleTabs().findIndex(
        (tab) => tab.fragment === this.activeSection(),
      );
      if (!group || index < 0 || group.activeTabIndex === index) return;
      group.activeTabIndex = index;
      this.tabGroupChanges()?.markForCheck();
    });
    // The tab strip is rendered by the shared tab group; mark each tab element with the section it
    // selects so tests and automation can address tabs by section id.
    afterEveryRender({ write: () => this.markTabElements() });
  }

  ngOnInit(): void {
    const routePhase = this.route.snapshot.data['phase'];
    if (isHearingPhase(routePhase)) {
      this.phase.set(routePhase);
    }

    const routeId =
      this.route.parent?.snapshot.paramMap.get('scheduledProceedingId') ??
      this.route.snapshot.paramMap.get('scheduledProceedingId');
    this.scheduledProceedingId.set(toPositiveInt(routeId));
    (this.route.parent?.paramMap ?? this.route.paramMap)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) =>
        this.scheduledProceedingId.set(
          toPositiveInt(params.get('scheduledProceedingId')),
        ),
      );
    this.legalReviewId.set(
      toPositiveInt(this.route.snapshot.queryParams?.['legalReview']),
    );
    this.route.queryParams
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) =>
        this.legalReviewId.set(toPositiveInt(params['legalReview'])),
      );
  }

  confirmNavigation(): boolean | Promise<boolean> {
    return this.preparationEditor()?.confirmNavigation() ?? true;
  }

  private markTabElements(): void {
    const host = this.tabHost()?.nativeElement;
    if (!host) return;
    const tabs = this.visibleTabs();
    host
      .querySelectorAll<HTMLElement>("[role='tab']")
      .forEach((element, index) => {
        const section = tabs[index]?.fragment;
        if (!section) return;
        const testId = `${HEARING_TAB_TEST_ID_PREFIX}${section}`;
        if (element.getAttribute('data-testid') !== testId)
          element.setAttribute('data-testid', testId);
        if (element.getAttribute('data-section') !== section)
          element.setAttribute('data-section', section);
      });
  }
}

function isHearingPhase(value: unknown): value is HearingPhase {
  return value === 'pre' || value === 'at' || value === 'post';
}

function toPositiveInt(value: unknown): number | null {
  const parsed =
    typeof value === 'number' ? value : Number(String(value ?? ''));
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}
